import React, { useEffect, useMemo, useState } from 'react';
import apiService from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';

// Editierbare Mail-Texte (alle Sprachen) — einfache UX:
// 1) Mail auswählen  2) Sprache wählen (DE/EN/ES/RU)  3) Felder bearbeiten
// 4) Live-Vorschau ansehen  5) Speichern / auf Standard zurücksetzen.
//
// Spiegelt das Backend (server/services/emailTemplates.js): gespeichert werden
// nur die Abweichungen vom Standard (Deep-Merge-Overrides).

type Lang = 'de' | 'en' | 'es' | 'ru';
const LANGS: { key: Lang; label: string }[] = [
  { key: 'de', label: 'Deutsch' },
  { key: 'en', label: 'English' },
  { key: 'es', label: 'Español' },
  { key: 'ru', label: 'Русский' },
];

interface FieldDef { key: string; label: string; multiline?: boolean; hint?: string }
interface GroupDef { path: string; title: string; note?: string; fields: FieldDef[] }

const F = {
  subject: { key: 'subject', label: 'Betreff' } as FieldDef,
  title: { key: 'title', label: 'Überschrift' } as FieldDef,
  intro: { key: 'intro', label: 'Einleitung', multiline: true } as FieldDef,
  outro: { key: 'outro', label: 'Abschlusstext', multiline: true } as FieldDef,
  text: { key: 'text', label: 'Text', multiline: true } as FieldDef,
  button: { key: 'button', label: 'Button-Beschriftung' } as FieldDef,
};

const GROUPS: GroupDef[] = [
  { path: 'status.confirmed', title: 'Terminbestätigung', note: 'Automatisch beim Bestätigen eines Termins.', fields: [F.subject, F.title, F.intro] },
  { path: 'status.rescheduled', title: 'Termin verschoben', note: 'Automatisch beim Verschieben.', fields: [F.subject, F.title, F.intro] },
  { path: 'status.cancelled', title: 'Terminstornierung', note: 'Automatisch beim Stornieren.', fields: [F.subject, F.title, F.intro] },
  { path: 'status.no_show', title: 'Verpasster Termin (No-Show)', fields: [F.subject, F.title, F.intro] },
  { path: 'status.reminder', title: 'Terminerinnerung', note: 'Automatische Erinnerung vor dem Termin.', fields: [F.subject, F.title, F.intro, F.outro] },
  { path: 'blocks.intake', title: 'Intake-Hinweis (in Bestätigung/Verschiebung)', note: 'Aufforderung, den Erhebungsbogen vorab auszufüllen.', fields: [F.text, F.button] },
  { path: 'blocks.manage', title: '„Termin verwalten"-Link', note: 'Absagen/Verschieben-Block.', fields: [F.text, F.button] },
  { path: 'blocks.leanofy', title: 'LEANOFY-Footer-Zeile', note: 'Dezente Hinweiszeile im Fuß der Patienten-Mails.', fields: [F.text] },
  {
    path: 'intakeInvitation',
    title: 'Intake-Einladung (manuell, mit QR-Code)',
    note: 'Wird vom Team aus dem Termin-Dialog versendet. Platzhalter: {name}, {when}.',
    fields: [
      { key: 'subject', label: 'Betreff' },
      { key: 'greeting', label: 'Anrede', hint: 'Platzhalter {name}' },
      { key: 'nameFallback', label: 'Anrede-Ersatz (ohne Namen)' },
      { key: 'intro', label: 'Einleitung', multiline: true },
      { key: 'obligationNote', label: 'Hinweis Sorgfaltspflicht', multiline: true },
      { key: 'appointmentLine', label: 'Terminzeile', hint: 'Platzhalter {when}' },
      { key: 'timeNote', label: 'Zeitaufwand-Hinweis', multiline: true },
      { key: 'ctaText', label: 'CTA-Text', multiline: true },
      { key: 'ctaButton', label: 'CTA-Button' },
      { key: 'qrIntro', label: 'QR-Hinweis', multiline: true },
      { key: 'confidentiality', label: 'Vertraulichkeitshinweis', multiline: true },
      { key: 'questions', label: 'Kontakt/Fragen (HTML erlaubt)', multiline: true },
      { key: 'closingWarm', label: 'Schlussgruß' },
      { key: 'signature', label: 'Signatur (HTML erlaubt)', multiline: true },
      { key: 'footerMotto', label: 'Footer-Motto' },
      { key: 'plainCta', label: 'CTA (Nur-Text-Version)' },
      { key: 'plainSignOff', label: 'Signatur (Nur-Text-Version)', multiline: true },
    ],
  },
];

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary outline-none';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

// Hilfsfunktionen für verschachtelte Pfade ("status.confirmed").
const getNode = (tree: any, path: string): any =>
  path.split('.').reduce((acc, k) => (acc ? acc[k] : undefined), tree);

const SAMPLE: Record<string, string> = { name: 'Max Mustermann', when: 'Montag, 1. Juli 2026, 10:00' };
const renderSample = (s: string): string =>
  String(s == null ? '' : s).replace(/\{(\w+)\}/g, (m, k) => (SAMPLE[k] != null ? SAMPLE[k] : m));

// Nur die vom Standard abweichenden Blätter rekursiv einsammeln (minimale Overrides).
const diffTree = (edited: any, defaults: any): any => {
  if (edited == null) return undefined;
  if (typeof edited !== 'object') {
    return edited !== defaults ? edited : undefined;
  }
  const out: any = {};
  let has = false;
  for (const k of Object.keys(edited)) {
    const d = diffTree(edited[k], defaults ? defaults[k] : undefined);
    if (d !== undefined) { out[k] = d; has = true; }
  }
  return has ? out : undefined;
};

const EmailTemplatesSettings: React.FC = () => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [data, setData] = useState<any>(null);          // editierbarer, gemergter Baum
  const [defaults, setDefaults] = useState<any>(null);   // Standardwerte (für Diff/Reset)
  const [groupPath, setGroupPath] = useState<string>(GROUPS[0].path);
  const [lang, setLang] = useState<Lang>('de');

  const group = useMemo(() => GROUPS.find(g => g.path === groupPath) || GROUPS[0], [groupPath]);

  useEffect(() => {
    let mounted = true;
    apiService.get<any>('/settings/email-templates')
      .then(resp => {
        if (mounted && resp.data?.success) {
          setData(resp.data.data.merged);
          setDefaults(resp.data.data.defaults);
        }
      })
      .catch(() => mounted && addToast('Mail-Texte konnten nicht geladen werden.', 'error'))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [addToast]);

  const getVal = (fieldKey: string, l: Lang): string => {
    const node = getNode(data, group.path);
    return (node && node[fieldKey] && node[fieldKey][l] != null) ? node[fieldKey][l] : '';
  };

  const setVal = (fieldKey: string, l: Lang, value: string) => {
    setData((prev: any) => {
      const next = structuredClone(prev);
      const node = getNode(next, group.path);
      if (!node[fieldKey]) node[fieldKey] = {};
      node[fieldKey][l] = value;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const patch = diffTree(data, defaults) || {};
      const resp = await apiService.put<any>('/settings/email-templates', { patch });
      if (resp.data?.success) {
        setData(resp.data.data.merged);
        addToast('Mail-Texte gespeichert.', 'success');
      } else {
        addToast(resp.data?.message || 'Speichern fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Speichern fehlgeschlagen.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (section: string | null) => {
    const label = section ? `„${group.title}"` : 'ALLE Mail-Texte';
    if (!window.confirm(`${label} auf den Standard zurücksetzen? Nicht gespeicherte Änderungen gehen verloren.`)) return;
    setResetting(true);
    try {
      const resp = await apiService.post<any>('/settings/email-templates/reset', { section });
      if (resp.data?.success) {
        setData(resp.data.data.merged);
        addToast('Auf Standard zurückgesetzt.', 'success');
      } else {
        addToast(resp.data?.message || 'Zurücksetzen fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Zurücksetzen fehlgeschlagen.', 'error');
    } finally {
      setResetting(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Lädt …</div>;
  if (!data) return <div className="p-6 text-gray-500">Keine Daten.</div>;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">E-Mail-Texte</h2>
        <p className="text-sm text-gray-600">
          Alle automatischen und manuellen Patienten-Mails — in allen Sprachen einseh- und bearbeitbar.
          Gespeichert werden nur Ihre Änderungen; der Rest bleibt auf dem Standard.
        </p>
      </div>

      {/* 1) Mail wählen */}
      <div>
        <label className={labelClass} htmlFor="mail-group-select">Welche Mail?</label>
        <select
          id="mail-group-select"
          value={groupPath}
          onChange={(e) => setGroupPath(e.target.value)}
          className={inputClass + ' max-w-md'}
        >
          {GROUPS.map(g => <option key={g.path} value={g.path}>{g.title}</option>)}
        </select>
        {group.note && <p className="mt-1 text-xs text-gray-500">{group.note}</p>}
      </div>

      {/* 2) Sprache wählen */}
      <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5">
        {LANGS.map(l => (
          <button
            key={l.key}
            type="button"
            onClick={() => setLang(l.key)}
            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
              lang === l.key ? 'bg-white shadow-sm text-brand-primary' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 3) Felder bearbeiten */}
        <div className="space-y-3">
          {group.fields.map(f => (
            <div key={f.key}>
              <label className={labelClass}>
                {f.label}
                {f.hint && <span className="ml-1 text-gray-400 font-normal">· {f.hint}</span>}
              </label>
              {f.multiline ? (
                <textarea
                  rows={3}
                  value={getVal(f.key, lang)}
                  onChange={(e) => setVal(f.key, lang, e.target.value)}
                  className={inputClass}
                />
              ) : (
                <input
                  type="text"
                  value={getVal(f.key, lang)}
                  onChange={(e) => setVal(f.key, lang, e.target.value)}
                  className={inputClass}
                />
              )}
            </div>
          ))}
        </div>

        {/* 4) Live-Vorschau */}
        <div>
          <div className="text-xs font-medium text-gray-600 mb-1">Vorschau ({LANGS.find(l => l.key === lang)?.label})</div>
          <div className="border border-gray-200 rounded-lg bg-gray-50 p-4 text-sm text-gray-800 space-y-2">
            {group.fields.map(f => {
              const raw = getVal(f.key, lang);
              if (!raw) return null;
              if (f.key === 'subject') {
                return <div key={f.key} className="text-xs text-gray-500">Betreff: <span className="text-gray-800 font-medium">{renderSample(raw)}</span></div>;
              }
              if (f.key === 'title') {
                return <div key={f.key} className="text-base font-semibold text-gray-900" dangerouslySetInnerHTML={{ __html: renderSample(raw) }} />;
              }
              if (f.key === 'button' || f.key === 'ctaButton') {
                return <div key={f.key}><span className="inline-block px-4 py-1.5 rounded-md bg-brand-primary text-white text-xs font-semibold">{renderSample(raw)}</span></div>;
              }
              return <p key={f.key} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: renderSample(raw) }} />;
            })}
          </div>
          <p className="mt-1 text-[11px] text-gray-400">Beispielwerte: {SAMPLE.name} · {SAMPLE.when}. HTML-Felder werden gerendert.</p>
        </div>
      </div>

      {/* 5) Aktionen */}
      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-100">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 rounded-md bg-brand-primary text-white text-sm font-medium disabled:opacity-50"
        >
          {saving ? 'Speichert …' : 'Speichern'}
        </button>
        <button
          type="button"
          onClick={() => handleReset(group.path)}
          disabled={resetting}
          className="px-3 py-2 rounded-md border border-gray-300 text-gray-700 text-sm disabled:opacity-50"
        >
          Diese Mail auf Standard
        </button>
        <button
          type="button"
          onClick={() => handleReset(null)}
          disabled={resetting}
          className="px-3 py-2 rounded-md border border-gray-300 text-gray-500 text-sm disabled:opacity-50"
        >
          Alle zurücksetzen
        </button>
      </div>
    </div>
  );
};

export default EmailTemplatesSettings;
